import { Transaction } from '../types';
import { GradingOrder } from './dataJoins';
import { PreferencesService } from './preferencesService';

/**
 * Service for calculating derived fields from user input data
 */
export class CalculationService {
  
  /**
   * Calculate grading cost for a transaction by linking to grading orders
   */
  static calculateGradingCost(
    transaction: Transaction, 
    gradingOrders: GradingOrder[],
    allTransactions: Transaction[]
  ): number {
    if (!transaction.grading_order_id) {
      return 0;
    }
    
    const gradingOrder = gradingOrders.find(
      order => order.order_id === transaction.grading_order_id
    );
    
    if (!gradingOrder || !gradingOrder.cost) {
      return 0;
    }
    
    // Get number of cards - either from grading order or calculate on the fly
    let numberOfCards = gradingOrder.number_of_cards;
    if (!numberOfCards) {
      // Calculate number of cards by counting transactions with this grading_order_id
      numberOfCards = allTransactions.filter(
        t => t.grading_order_id === transaction.grading_order_id
      ).length;
    }
    
    if (!numberOfCards || numberOfCards === 0) {
      return 0;
    }
    
    // Calculate cost per card from grading order
    return gradingOrder.cost / numberOfCards;
  }
  
  /**
   * Calculate number of cards for a grading order by counting linked transactions
   * If user has provided a value, use that instead
   */
  static calculateNumberOfCards(
    gradingOrder: GradingOrder,
    transactions: Transaction[]
  ): number {
    // If user has provided a value, use that
    if (gradingOrder.number_of_cards !== undefined && gradingOrder.number_of_cards !== null) {
      return gradingOrder.number_of_cards;
    }
    
    // Otherwise calculate from linked transactions
    return transactions.filter(
      transaction => transaction.grading_order_id === gradingOrder.order_id
    ).length;
  }
  
  /**
   * Calculate cost per card for a grading order
   */
  static calculateCostPerCard(
    gradingOrder: GradingOrder,
    transactions: Transaction[]
  ): number {
    const numberOfCards = this.calculateNumberOfCards(gradingOrder, transactions);
    if (numberOfCards === 0 || !gradingOrder.cost) {
      return 0;
    }
    
    return gradingOrder.cost / numberOfCards;
  }
  
  /**
   * Calculate total gems for a grading order (cards with high grades)
   */
  static calculateTotalGems(
    gradingOrder: GradingOrder,
    transactions: Transaction[]
  ): number {
    const linkedTransactions = transactions.filter(
      transaction => transaction.grading_order_id === gradingOrder.order_id
    );
    
    return linkedTransactions.filter(transaction => {
      if (!transaction.grade) return false;
      
      // Extract company and grade from the grade string
      const { company, grade } = this.parseGrade(transaction.grade);
      
      // Use configurable gem definitions
      return PreferencesService.isGemGrade(company, grade);
    }).length;
  }
  
  /**
   * Calculate gem rate percentage for a grading order
   */
  static calculateGemRate(
    gradingOrder: GradingOrder,
    transactions: Transaction[]
  ): number {
    const numberOfCards = this.calculateNumberOfCards(gradingOrder, transactions);
    const totalGems = this.calculateTotalGems(gradingOrder, transactions);
    
    if (numberOfCards === 0) return 0;
    
    return (totalGems / numberOfCards) * 100;
  }
  
  /**
   * Calculate turnaround time for a grading order in days
   */
  static calculateTurnaroundTime(gradingOrder: GradingOrder): number {
    if (!gradingOrder.return_date || !gradingOrder.submission_date) {
      return 0;
    }
    
    const submissionDate = new Date(gradingOrder.submission_date);
    const returnDate = new Date(gradingOrder.return_date);
    
    const diffTime = Math.abs(returnDate.getTime() - submissionDate.getTime());
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  }
  
  /**
   * Calculate net profit for a transaction
   */
  static calculateNetProfit(transaction: Transaction): number {
    if (!transaction.sale_price) return 0;
    
    const salePrice = typeof transaction.sale_price === 'string' 
      ? parseFloat(transaction.sale_price.replace(/[$,]/g, '')) 
      : transaction.sale_price;
    
    const totalCost = this.calculateTotalCost(transaction);
    const marketplaceFees = transaction.marketplace_fees 
      ? (typeof transaction.marketplace_fees === 'string' 
          ? parseFloat(transaction.marketplace_fees.replace(/[$,]/g, '')) 
          : transaction.marketplace_fees)
      : 0;
    const shippingFees = transaction.shipping_fees 
      ? (typeof transaction.shipping_fees === 'string' 
          ? parseFloat(transaction.shipping_fees.replace(/[$,]/g, '')) 
          : transaction.shipping_fees)
      : 0;
    
    return salePrice - totalCost - marketplaceFees - shippingFees;
  }
  
  
  /**
   * Calculate ROI for a transaction (net profit / total cost)
   */
  static calculateROI(transaction: Transaction): number {
    const totalCost = this.calculateTotalCost(transaction);
    const netProfit = this.calculateNetProfit(transaction);
    
    if (totalCost === 0) return 0;
    return (netProfit / totalCost) * 100;
  }
  
  /**
   * Calculate transaction status based on available data
   */
  static calculateStatus(
    transaction: Transaction,
    linkedGradingOrder?: GradingOrder
  ): 'purchased' | 'at_grading' | 'sold' {
    // If sold, status is 'sold'
    if (transaction.sale_date && transaction.sale_date.toString().trim() !== '') {
      return 'sold';
    }
    
    // If has grading order but no sale, status is 'at_grading'
    if (transaction.grading_order_id && transaction.grading_order_id.toString().trim() !== '') {
      const hasReturnDate =
        linkedGradingOrder?.return_date &&
        linkedGradingOrder.return_date.toString().trim() !== '';
      if (!hasReturnDate) {
        return 'at_grading';
      }
    }
    
    // Otherwise, status is 'purchased'
    return 'purchased';
  }
  
  /**
   * Calculate total cost for a transaction (purchase + grading)
   */
  static calculateTotalCost(transaction: Transaction): number {
    const purchaseCost = typeof transaction.cost === 'string' 
      ? parseFloat(transaction.cost.replace(/[$,]/g, '')) 
      : transaction.cost || 0;
    
    const gradingCost = transaction.grading_cost 
      ? (typeof transaction.grading_cost === 'string' 
          ? parseFloat(transaction.grading_cost.replace(/[$,]/g, '')) 
          : transaction.grading_cost)
      : 0;
    
    return purchaseCost + gradingCost;
  }
  
  /**
   * Extract sport from card name using pattern matching
   */
  static extractSportFromCard(cardName: string): string {
    if (!cardName) return '';
    
    const sportPatterns = {
      'MLB': ['baseball', 'mlb', 'rookie', 'rc', 'topps', 'bowman', 'donruss'],
      'NFL': ['football', 'nfl', 'rookie', 'rc', 'panini', 'score', 'contenders'],
      'NBA': ['basketball', 'nba', 'rookie', 'rc', 'panini', 'hoops', 'prizm'],
      'NHL': ['hockey', 'nhl', 'rookie', 'rc', 'upper deck', 'young guns'],
      'Soccer': ['soccer', 'football', 'fifa', 'world cup', 'champions league'],
      'Tennis': ['tennis', 'wimbledon', 'us open', 'french open'],
      'Golf': ['golf', 'pga', 'masters', 'us open'],
      'Other': ['racing', 'nascar', 'indy', 'f1', 'boxing', 'mma', 'ufc']
    };
    
    const lowerCardName = cardName.toLowerCase();
    
    for (const [sport, patterns] of Object.entries(sportPatterns)) {
      if (patterns.some(pattern => lowerCardName.includes(pattern))) {
        return sport;
      }
    }
    
    return 'Other';
  }
  
  /**
   * Parse a grade string to extract company and grade
   * Examples: "PSA 10" -> {company: "PSA", grade: "10"}
   *           "BGS 9.5" -> {company: "BGS", grade: "9.5"}
   */
  private static parseGrade(gradeString: string): { company: string; grade: string } {
    if (!gradeString) return { company: '', grade: '' };
    
    // Common grading companies
    const companies = ['PSA', 'BGS', 'SGC', 'CSG', 'CGC', 'HGA', 'GMA', 'ISA'];
    
    for (const company of companies) {
      if (gradeString.toUpperCase().includes(company)) {
        // Extract the grade number after the company name
        const regex = new RegExp(`${company}\\s*(\\d+(?:\\.\\d+)?)`, 'i');
        const match = gradeString.match(regex);
        if (match) {
          return { company, grade: match[1] };
        }
      }
    }
    
    // Fallback: try to extract any number as grade
    const gradeMatch = gradeString.match(/(\d+(?:\.\d+)?)/);
    if (gradeMatch) {
      return { company: 'Unknown', grade: gradeMatch[1] };
    }
    
    return { company: 'Unknown', grade: '' };
  }
  
  /**
   * Enrich transactions with calculated fields
   */
  static enrichTransactions(
    transactions: Transaction[],
    gradingOrders: GradingOrder[]
  ): Transaction[] {
    return transactions.map(transaction => {
      const gradingCost = this.calculateGradingCost(transaction, gradingOrders, transactions);
      
      // Find the linked grading order for additional calculated fields
      const linkedGradingOrder = gradingOrders.find(
        order => order.order_id === transaction.grading_order_id
      );
      
      const transactionWithGradingCost = {
        ...transaction,
        grading_cost: gradingCost
      };
      
      const enrichedTransaction: any = {
        ...transaction,
        // Calculate sport if not provided by user
        sport: transaction.sport || this.extractSportFromCard(transaction.card || ''),
        // Calculate grading cost from linked grading order
        grading_cost: gradingCost,
        // Calculate total cost (purchase + grading)
        calculated_total_cost: this.calculateTotalCost(transactionWithGradingCost),
        // Calculate net profit (sale price - total cost - marketplace fees - shipping fees)
        calculated_net_profit: this.calculateNetProfit(transactionWithGradingCost),
        // Calculate ROI (net profit / total cost)
        roi: this.calculateROI(transactionWithGradingCost),
        // Calculate status based on transaction data
        status: this.calculateStatus(transaction, linkedGradingOrder)
      };
      
      // Add grading order calculated fields if linked
      if (linkedGradingOrder) {
        enrichedTransaction.grading_cost_per_card = this.calculateCostPerCard(linkedGradingOrder, transactions);
        enrichedTransaction.grading_total_gems = this.calculateTotalGems(linkedGradingOrder, transactions);
        enrichedTransaction.grading_gem_rate = this.calculateGemRate(linkedGradingOrder, transactions);
        enrichedTransaction.grading_turnaround_time = this.calculateTurnaroundTime(linkedGradingOrder);
      }
      
      return enrichedTransaction;
    });
  }
  
  /**
   * Enrich grading orders with calculated fields
   */
  static enrichGradingOrders(
    gradingOrders: GradingOrder[],
    transactions: Transaction[]
  ): GradingOrder[] {
    return gradingOrders.map(gradingOrder => {
      const numberOfCards = this.calculateNumberOfCards(gradingOrder, transactions);
      const costPerCard = this.calculateCostPerCard(gradingOrder, transactions);
      const totalGems = this.calculateTotalGems(gradingOrder, transactions);
      const gemRate = this.calculateGemRate(gradingOrder, transactions);
      const turnaroundTime = this.calculateTurnaroundTime(gradingOrder);
      
      return {
        ...gradingOrder,
        number_of_cards: numberOfCards,
        cost_per_card: costPerCard,
        total_gems: totalGems,
        gem_rate: gemRate,
        turnaround_time: turnaroundTime
      };
    });
  }
}
